import { useCallback, useMemo, useState } from 'react';
import { Network } from '@phosphor-icons/react';
import type { ComponentDocument } from 'common/stack/ComponentDocuments';
import { parsePackageUrl } from 'common/stack/stackUtils';
import { StackComponentDialog } from 'client/pages/stack/components/StackComponentDialog';
import { StackContentList } from 'client/pages/stack/components/StackContentList';

export interface StackServicesProps {
  addons: { name: string; identifier: string; package: string }[];
  stack: { identifier: string; details: ComponentDocument }[];
  onIdentifier: (value: { identifier: string; value: string }) => void;
  onVersion: (value: { identifier: string; value: string }) => void;
  onRemove: (identifier: string) => void;
}

export const StackServices = ({ addons, stack, onIdentifier, onVersion, onRemove }: StackServicesProps) => {
  const [identifier, setIdentifier] = useState<string | null>(null);

  const component = useMemo(() => {
    if (!identifier) return null;

    const component = addons.find((component) => component.identifier === identifier);
    if (!component) return null;

    const { ref = 'main' } = parsePackageUrl(component.package);

    const details = stack.find((item) => item.identifier === identifier);
    if (!details) return null;

    return {
      identifier: component.identifier,
      version: ref,
      type: 'addon' as const,
      details: details.details,
    };
  }, [identifier, addons, stack]);

  const handleClick = ({ identifier }: { identifier: string }) => setIdentifier(identifier);

  const handleIdentifierChange = useCallback(
    (value: string) => component && onIdentifier({ identifier: component.identifier, value }),
    [component, onIdentifier]
  );

  const handleVersionChange = useCallback(
    (value: string) => component && onVersion({ identifier: component.identifier, value }),
    [component, onVersion]
  );

  const items = useMemo(
    () =>
      addons.map((addon) => ({
        label: addon.identifier,
        component: addon,
      })),
    [addons]
  );

  return (
    <>
      <StackContentList
        title={
          <>
            <Network weight="bold" /> Services
          </>
        }
        items={items}
        help="Editing the addon identifier will change the internal address where it can be reached from."
        onClick={handleClick}
      />
      <StackComponentDialog
        component={component}
        onIdentifier={handleIdentifierChange}
        onVersion={handleVersionChange}
        onRemove={onRemove}
        onClose={() => setIdentifier(null)}
      />
    </>
  );
};
